/**
 * File Utility Functions for CANmail Attachments
 *
 * This module provides utility functions for working with file attachments,
 * including size calculations, formatting, and validation.
 */

import { formatFileSize } from 'utils/formatFileSize';

/**
 * Calculate size of base64 encoded data
 *
 * @param {string} base64Data - Base64 string
 * @returns {number} Size in bytes
 */
export function calculateBase64Size(base64Data) {
  if (!base64Data || typeof base64Data !== 'string') {
    return 0;
  }

  try {
    const cleanData = base64Data.trim();
    // Base64 encoding increases size by ~33%, so decode size is (length * 3) / 4
    return (cleanData.length * 3) / 4;
  } catch (error) {
    console.error('Error calculating base64 size:', error);
    return 0;
  }
}

/**
 * Calculate total size of attachments array
 *
 * @param {Array} attachments - Array of {name, type, data} objects
 * @returns {number} Total size in bytes
 */
export function calculateTotalAttachmentSize(attachments) {
  if (!attachments || attachments.length === 0) {
    return 0;
  }

  return attachments.reduce((total, attachment) => {
    if (attachment && attachment.data) {
      return total + calculateBase64Size(attachment.data);
    }
    return total;
  }, 0);
}

/**
 * Validate attachment size against limit
 *
 * @param {Array} attachments - Array of {name, type, data} objects
 * @param {number} limitBytes - Size limit in bytes (default 10MB)
 * @returns {Object} { valid: boolean, totalSize: number, limit: number, message: string }
 */
export function validateAttachmentSize(attachments, limitBytes = 10 * 1024 * 1024) {
  const totalSize = calculateTotalAttachmentSize(attachments);
  const valid = totalSize <= limitBytes;

  return {
    valid,
    totalSize,
    limit: limitBytes,
    message: valid
      ? `Total size: ${formatFileSize(totalSize)} / ${formatFileSize(limitBytes)}`
      : `Total size (${formatFileSize(totalSize)}) exceeds limit of ${formatFileSize(limitBytes)}`
  };
}

/**
 * Convert File object to base64 attachment
 *
 * @param {File} file - File object from input
 * @returns {Promise<Object>} Promise resolving to {name, type, data}
 */
export async function convertFileToBase64Attachment(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        // Remove the data:*/*;base64, prefix
        const base64 = reader.result.split(',')[1];

        resolve({
          name: file.name,
          type: file.type,
          data: base64
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Create a download link for a base64 attachment
 *
 * @param {Object} attachment - Attachment object {name, type, data}
 * @returns {string} Blob URL for download
 */
export function createAttachmentDownloadUrl(attachment) {
  try {
    // Convert base64 to blob
    const byteCharacters = atob(attachment.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: attachment.type });

    // Create blob URL
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error creating download URL:', error);
    return null;
  }
}

/**
 * Download an attachment
 *
 * @param {Object} attachment - Attachment object {name, type, data}
 */
export function downloadAttachment(attachment) {
  try {
    const url = createAttachmentDownloadUrl(attachment);
    if (!url) {
      throw new Error('Failed to create download URL');
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up blob URL
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error downloading attachment:', error);
    throw error;
  }
}
